// First-run setup-wizard client (item 76 of PRODUCT-ROADMAP.md).
// Backs the SetupWizard.tsx page + the /admin/system/setup re-run
// surface. The boolean `setup_required` is also surfaced on the
// public-config endpoint for the auto-redirect; this client covers
// the admin-only state mutation surface.

import useSWR from 'swr';

import { parseErrorResponse } from './problem';

export type SetupStep = 'branding' | 'admin' | 'update_check' | 'first_project' | 'sso';

export interface SetupState {
  setup_started_at?: string;
  setup_completed_at?: string;
  step_branding_at?: string;
  step_admin_at?: string;
  // Item 90 Phase B: explicit operator opt-in/opt-out for the
  // update-check egress channel. NULL until the operator clicks one
  // of the three buttons in the wizard.
  step_update_check_at?: string;
  step_first_project_at?: string;
  // Item 216 batch 1: the operator walked the SSO/IdP-preset step.
  step_sso_at?: string;
  completed_by?: string;
  updated_at: string;
  setup_required: boolean;
}

const fetcher = async (url: string) => {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
};

export function useSetupStatus() {
  return useSWR<SetupState>('/api/admin/setup/status', fetcher);
}

async function postJSON(path: string, body?: unknown): Promise<void> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok && res.status !== 204) throw await parseErrorResponse(res);
}

export const startSetup = () => postJSON('/api/admin/setup/start');
export const markSetupStep = (step: SetupStep) => postJSON('/api/admin/setup/step', { step });
export const completeSetup = () => postJSON('/api/admin/setup/complete');
export const resetSetup = () => postJSON('/api/admin/setup/reset');
